/** Utility constants and functions for task form state. */
import { TIME_SLOT_MINS, DEFAULT_TASK_DURATION_MINS } from '@/config/constants';

export const emptyTaskState = {
  title: '',
  description: '',
  completed: false,
  duration: DEFAULT_TASK_DURATION_MINS,
  projects: [],
  contexts: [],
  date: '',
  time: '',
  person: null,
  status: 'Active',
  parentId: null,
  recurrence: null,
};

export const MIN_DURATION = TIME_SLOT_MINS;
export const MINUTES_IN_DAY = 1440;

export const formatTime = (totalMinutes) => {
  const nh = Math.floor((totalMinutes % MINUTES_IN_DAY) / 60);
  const nm = totalMinutes % 60;
  return `${String(nh).padStart(2, '0')}:${String(nm).padStart(2, '0')}`;
};

export const sortParentOptions = (allTasks) => {
  if (!allTasks) return [];
  return [...allTasks].sort((a, b) => {
    const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
    const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
    return dateA !== dateB
      ? dateB - dateA
      : (a.title || '').localeCompare(b.title || '');
  });
};

export const processParentChange = (prev, optId, allTasks) => {
  const updated = { ...prev, parentId: optId ?? null };
  const parent = optId ? allTasks.find((t) => t.id === optId) : null;
  if (parent?.status) updated.status = parent.status;
  if (
    parent?.contexts?.length > 0 &&
    (!prev.contexts || prev.contexts.length === 0)
  ) {
    updated.contexts = [...parent.contexts];
  }
  return updated;
};

/**
 * Filters and sorts subtasks for the task form.
 */
export const getFormSubtasks = (initialTask, allTasks) => {
  if (!initialTask?.materializedPath || !allTasks) return [];
  const prefix = initialTask.materializedPath;
  return allTasks
    .filter(
      (t) => t.materializedPath?.startsWith(prefix) && t.id !== initialTask.id,
    )
    .sort((a, b) => {
      if (a.completed !== b.completed) return a.completed ? 1 : -1;
      const pathA = a.materializedPath || '';
      const pathB = b.materializedPath || '';
      return pathA < pathB ? -1 : pathA > pathB ? 1 : 0;
    });
};

/**
 * Calculates the total duration of incomplete subtasks.
 */
export const calculateIncompleteSubtasksDuration = (subtasks) => {
  return subtasks
    .filter((t) => !t.completed)
    .reduce((sum, t) => sum + (t.duration || 0), 0);
};

/**
 * Simplified validation for task form data (used in some tests).
 */
export const validateTaskForm = (data) => {
  const errors = {};
  const title = data.title?.trim();

  if (!title) {
    errors.title = 'Title is required';
  } else if (title.length > 100) {
    errors.title = 'Title is too long (max 100 characters).';
  }

  if (data.time && !/^\d{2}:\d{2}$/.test(data.time)) {
    errors.time = 'Time must be in HH:mm format';
  }

  const success = Object.keys(errors).length === 0;
  return {
    success,
    errors,
    data: success ? { ...data, title, time: data.time || null } : null,
  };
};
